/**
 * Unified Analytics Service — GTM (GA4) + Meta Pixel dual-fire tracking
 *
 * Custom dataLayer event names (must match GTM Custom Event triggers exactly):
 *
 * | event                    | Where / when                         | Typical payload |
 * |--------------------------|--------------------------------------|-----------------|
 * | login                    | LoginForm — successful login         | method          |
 * | sign_up                  | RegisterForm — successful register   | method          |
 * | purchase                 | StripeSubscriptionPage — paid return | value, currency, transaction_id |
 * | subscription_activated   | SuccessDisplay — success screen      | value, currency, transaction_id |
 * | page_view                | usePageTracking — route change       | page_path, page_title, page_location |
 *
 * Each `trackEvent` call: pushToDataLayer → sendGa4ViaGtag (if REACT_APP_GA_MEASUREMENT_ID)
 * → sendMetaConversion. If you also fire GA4 Event tags in GTM on the same names, avoid
 * double-counting (use either gtag from the app or GTM tags, not both for the same event).
 */

import ReactPixel from 'react-facebook-pixel';

const GTM_STORAGE_KEY = 'arbiwin_gtm_initialized';
/** Same key as consentService — avoid importing consentService (circular deps). */
const CONSENT_STORAGE_KEY = 'arbiwin_cookie_consent';
const GTAG_BOOTSTRAP_SCRIPT_ID = 'arbiwin-ga4-gtag-bootstrap';
let metaPixelReady = false;
let gtagBootstrapDone = false;

/**
 * Initialize GTM by injecting script/noscript tags.
 * Meta conversions use react-facebook-pixel only (see ensureMetaPixelForConversions).
 * @param {string} gtmId - GTM container ID (e.g. GTM-XXXXXXX)
 */
function initialize(gtmId) {
  if (typeof window === 'undefined' || !gtmId || typeof gtmId !== 'string') {
    return;
  }
  const id = gtmId.trim();
  if (!id.startsWith('GTM-')) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] Invalid GTM ID format. Expected GTM-XXXXXXX.');
    }
    return;
  }
  if (isInitialized()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'gtm.js',
    'gtm.start': new Date().getTime()
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  script.id = 'gtm-script';
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  try {
    sessionStorage.setItem(GTM_STORAGE_KEY, '1');
  } catch (e) {
    // ignore
  }

  scheduleGrantAnalyticsConsent();
}

/**
 * Ensure ReactPixel is initialized exactly once (no manual window.fbq stubs).
 */
function ensureMetaPixelForConversions() {
  const pixelId = process.env.REACT_APP_META_PIXEL_ID;
  if (!pixelId || metaPixelReady) {
    return;
  }

  ReactPixel.init(pixelId, undefined, { autoConfig: false, debug: false });
  metaPixelReady = true;
}

/**
 * Push an event to the GTM dataLayer.
 * Creates the dataLayer array when missing so events queued before consent
 * are processed once GTM loads.
 */
const pushToDataLayer = (eventName, data = {}) => {
  if (typeof window === 'undefined') return;
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push({ event: eventName, ...data });
};

/**
 * Load gtag.js + stub if window.gtag is missing. If GTM already defined gtag,
 * we still use it but must pass send_to on each event and sync consent (below).
 */
function ensureGtagJsLoaded() {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId || typeof window === 'undefined') {
    return;
  }
  if (typeof window.gtag === 'function') {
    gtagBootstrapDone = true;
    return;
  }
  if (gtagBootstrapDone || document.getElementById(GTAG_BOOTSTRAP_SCRIPT_ID)) {
    gtagBootstrapDone = true;
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.id = GTAG_BOOTSTRAP_SCRIPT_ID;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  gtagBootstrapDone = true;
}

/**
 * When the user has accepted analytics cookies, align with Consent Mode v2 so
 * gtag does not drop hits (GTM often defaults analytics_storage to denied until
 * this runs).
 */
function hasAnalyticsCookieConsent() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

function grantAnalyticsStorageConsent() {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId || typeof window === 'undefined' || !hasAnalyticsCookieConsent()) {
    return;
  }
  ensureGtagJsLoaded();
  if (typeof window.gtag !== 'function') {
    return;
  }
  try {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted'
    });
  } catch (e) {
    // ignore
  }
}

/**
 * Defer consent sync until after GTM/gtag attach (same tick is often too early).
 */
function scheduleGrantAnalyticsConsent() {
  if (typeof window === 'undefined') {
    return;
  }
  const run = () => grantAnalyticsStorageConsent();
  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      setTimeout(run, 0);
    });
  } else {
    setTimeout(run, 0);
  }
}

/**
 * Send the same event to GA4 via gtag. Uses send_to so the hit targets this
 * property when multiple tags/gtag configs exist (GTM + Google tag).
 */
function sendGa4ViaGtag(eventName, data = {}) {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId || typeof window === 'undefined') {
    return;
  }
  if (!hasAnalyticsCookieConsent()) {
    return;
  }

  ensureGtagJsLoaded();
  grantAnalyticsStorageConsent();
  if (typeof window.gtag !== 'function') {
    return;
  }

  const params = {
    ...data,
    send_to: data.send_to || measurementId
  };
  if (typeof window !== 'undefined' && window.location) {
    if (!params.page_location) params.page_location = window.location.href;
    if (!params.page_path) params.page_path = window.location.pathname;
  }
  Object.keys(params).forEach((k) => {
    if (params[k] === undefined) {
      delete params[k];
    }
  });
  window.gtag('event', eventName, params);
}

/**
 * Send conversion to Meta via ReactPixel (after ensureMetaPixelForConversions).
 */
function sendMetaConversion(eventName, data) {
  const pixelId = process.env.REACT_APP_META_PIXEL_ID;
  if (!pixelId || typeof window === 'undefined') {
    return;
  }

  ensureMetaPixelForConversions();

  const metaData = { ...data };
  delete metaData.send_to;

  if (eventName === 'sign_up') {
    ReactPixel.track('CompleteRegistration', metaData);
  } else if (eventName === 'purchase') {
    ReactPixel.track('Purchase', metaData);
  } else if (eventName === 'subscription_activated') {
    ReactPixel.trackCustom('SubscriptionActivated', metaData);
  } else if (eventName === 'login') {
    ReactPixel.trackCustom('Login', metaData);
  } else {
    ReactPixel.trackCustom(eventName, metaData);
  }
}

/**
 * Unified event tracker — dataLayer always; Meta only for non-page_view events.
 *
 * page_view: dataLayer only (GTM should map to GA4 + Meta Page View once per view).
 * Do not call ReactPixel.pageView here — duplicates GTM's Meta Page View and triggers
 * Meta Pixel Helper warnings.
 *
 * @param {string} eventName - Internal event name (snake_case)
 * @param {Object} [data]    - Event parameters
 */
export const trackEvent = (eventName, data = {}) => {
  pushToDataLayer(eventName, data);

  if (eventName === 'page_view') {
    return;
  }

  sendGa4ViaGtag(eventName, data);
  sendMetaConversion(eventName, data);
};

/**
 * Check if GTM has been initialized this session.
 * @returns {boolean}
 */
function isInitialized() {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(GTM_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export const analyticsService = {
  initialize,
  trackEvent,
  pushToDataLayer,
  isInitialized,
  /** Call after consent if GTM was already initialized without schedule (edge cases). */
  grantAnalyticsStorageConsent
};
